<template>
  <div class="vl-page">
    <!-- brand panel -->
    <aside class="vl-brand">
      <svg class="vl-brand__pattern" viewBox="0 0 600 800" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <g fill="none" stroke="#fff" stroke-opacity=".09" stroke-width="1.5">
          <circle cx="520" cy="120" r="90" />
          <circle cx="520" cy="120" r="150" />
          <circle cx="520" cy="120" r="210" />
          <circle cx="40" cy="720" r="110" />
          <circle cx="40" cy="720" r="190" />
          <circle cx="40" cy="720" r="270" />
        </g>
        <g fill="#fff" fill-opacity=".10">
          <path v-for="(c, i) in crosses" :key="i" :transform="`translate(${c[0]} ${c[1]}) scale(${c[2]})`" d="M-3 -9h6v6h6v6h-6v6h-6v-6h-6v-6h6z" />
        </g>
      </svg>

      <div class="vl-brand__top">
        <span class="vl-logo">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M10 3.5h4a1 1 0 0 1 1 1V9h4.5a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H15v4.5a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1V15H4.5a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1H9V4.5a1 1 0 0 1 1-1z" />
          </svg>
        </span>
        <span class="vl-wordmark">Vivo<small>{{ $t("common.portalSub") }}</small></span>
      </div>

      <div class="vl-brand__body">
        <h2>{{ $t("login.brandTitle") }}</h2>
        <p>{{ $t("login.brandText") }}</p>
        <ul class="vl-features">
          <li v-for="k in ['feature1', 'feature2', 'feature3']" :key="k">
            <span class="vl-features__tick">
              <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4.5 10.5l3.5 3.5 7.5-8" /></svg>
            </span>
            {{ $t("login." + k) }}
          </li>
        </ul>
      </div>

      <div class="vl-brand__foot">&copy; {{ year }} Vivo · {{ $t("common.company") }}</div>
    </aside>

    <!-- form panel -->
    <main class="vl-panel">
      <div class="vl-lang">
        <button type="button" :class="{ on: lang === 'ar' }" @click="changeLang('ar')">عربي</button>
        <button type="button" :class="{ on: lang === 'en' }" @click="changeLang('en')">English</button>
      </div>

      <form class="vl-card" novalidate @submit.prevent="loginForm">
        <div class="vl-card__logo">
          <span class="vl-logo vl-logo--sm">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M10 3.5h4a1 1 0 0 1 1 1V9h4.5a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H15v4.5a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1V15H4.5a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1H9V4.5a1 1 0 0 1 1-1z" />
            </svg>
          </span>
          <span class="vl-wordmark vl-wordmark--dark">Vivo<small>{{ $t("common.portalSub") }}</small></span>
        </div>

        <h1>{{ $t("login.welcome") }}</h1>
        <p class="vl-sub">{{ $t("login.subtitle") }}</p>

        <div v-if="error" class="vl-error" role="alert">
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M10 2.5l8 14H2l8-14zM10 8v4M10 14.2v.1" /></svg>
          {{ error }}
        </div>

        <label class="vl-field">
          <span>{{ $t("login.email") }}</span>
          <input
            v-model.trim="user.userNameOrEmail"
            type="email"
            name="email"
            autocomplete="username"
            dir="ltr"
            :placeholder="$t('login.emailPlaceholder')"
            :disabled="loading"
            required
          />
        </label>

        <label class="vl-field">
          <span>{{ $t("login.password") }}</span>
          <div class="vl-field__pw">
            <input
              v-model="user.password"
              :type="showPw ? 'text' : 'password'"
              name="password"
              autocomplete="current-password"
              dir="ltr"
              :placeholder="$t('login.passwordPlaceholder')"
              :disabled="loading"
              required
            />
            <button
              type="button"
              class="vl-field__eye"
              :aria-label="showPw ? $t('login.hidePassword') : $t('login.showPassword')"
              @click="showPw = !showPw"
            >
              <svg v-if="!showPw" viewBox="0 0 24 24" aria-hidden="true"><path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>
              <svg v-else viewBox="0 0 24 24" aria-hidden="true"><path d="M3 3l18 18M10.6 6.2A9.9 9.9 0 0 1 12 6c6.4 0 10 6 10 6a17 17 0 0 1-3.2 3.8M6.5 7.6C3.9 9.3 2 12 2 12s3.6 6 10 6c1.5 0 2.8-.3 4-.8" /></svg>
            </button>
          </div>
        </label>

        <button class="vl-submit" type="submit" :disabled="loading || !canSubmit">
          <span v-if="loading" class="vl-spin" aria-hidden="true"></span>
          {{ loading ? $t("login.signingIn") : $t("login.submit") }}
        </button>
      </form>
    </main>
  </div>
</template>

<script>
import { apiPost } from "../../api/vivo/http";
import * as tokenStore from "../../api/vivo/token-store";
import { sessionFromToken } from "../../api/vivo/jwt";

export default {
  name: "auth-layout",
  data() {
    return {
      year: new Date().getFullYear(),
      loading: false,
      showPw: false,
      error: "",
      // decorative plus-signs in the brand panel: [x, y, scale]
      crosses: [
        [90, 140, 2.2], [300, 70, 1.4], [470, 300, 2.6], [120, 420, 1.6],
        [400, 520, 1.8], [250, 640, 2.4], [540, 700, 1.4], [60, 560, 1.2],
      ],
      user: {
        userNameOrEmail: "",
        password: "",
      },
    };
  },
  computed: {
    lang() {
      return (this.$root.$i18n && this.$root.$i18n.locale) || "ar";
    },
    canSubmit() {
      return !!(this.user.userNameOrEmail && this.user.password);
    },
  },
  methods: {
    changeLang(lang) {
      this.$changeRtl(lang);
      this.$root.$i18n.locale = lang;
    },
    async loginForm() {
      // §4 auth seam (A2): Vivo logs in by EMAIL. The raw body is `{accessToken}`
      // (no envelope); the refresh token is set as an httpOnly cookie by the
      // backend. The access token lives in memory only; a non-sensitive marker
      // ({companyId, role, email} — NEVER the token) persists for the router
      // guard + reload-safe HR scoping.
      const email = (this.user.userNameOrEmail || "").trim();
      const password = this.user.password;
      if (this.loading) return;
      this.loading = true;
      this.error = "";
      try {
        const data = await apiPost("/auth/login", { email, password });
        const token = data && data.accessToken;
        if (!token) {
          throw new Error("login response is missing accessToken");
        }
        tokenStore.setAccessToken(token);
        const session = sessionFromToken(token, { email });
        tokenStore.setSession(session);
        localStorage.setItem(
          "user",
          JSON.stringify({
            companyId: session.companyId,
            role: session.role,
            email,
          })
        );
        if (!localStorage.getItem("lang")) {
          localStorage.setItem("lang", (this.$root.$i18n && this.$root.$i18n.locale) || "ar");
        }
        this.$router.push("/home");
      } catch (error) {
        const status = error && error.response && error.response.status;
        this.error =
          status === 400 || status === 401
            ? this.$t("login.invalid")
            : (error &&
                error.response &&
                error.response.data &&
                error.response.data.message) ||
              this.$t("login.errorTitle");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.vl-page {
  --vl-teal: #0e7c86;
  --vl-teal-2: #0a5b63;
  --vl-teal-3: #07444b;
  --vl-ink: #14202b;
  --vl-ink-2: #4c5b6d;
  --vl-line: #dde4ec;
  min-height: 100vh;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
  background: #f5f8fa;
  color: var(--vl-ink);
  font-family: "Zain", "Cairo", "Segoe UI", Tahoma, sans-serif;
}

/* ───────── brand panel ───────── */
.vl-brand {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 32px;
  padding: 44px 56px;
  color: #fff;
  background: linear-gradient(155deg, var(--vl-teal) 0%, var(--vl-teal-2) 55%, var(--vl-teal-3) 100%);
}
.vl-brand__pattern {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}
.vl-brand__top,
.vl-brand__body,
.vl-brand__foot { position: relative; }
.vl-brand__top { display: flex; align-items: center; gap: 14px; }
.vl-brand__body h2 {
  margin: 0 0 14px;
  max-width: 460px;
  font-size: 34px;
  line-height: 1.35;
  font-weight: 700;
  letter-spacing: -0.01em;
}
.vl-brand__body p {
  margin: 0 0 30px;
  max-width: 440px;
  font-size: 16px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.82);
}
.vl-brand__foot { font-size: 13px; color: rgba(255, 255, 255, 0.6); }

.vl-logo {
  display: inline-grid;
  place-items: center;
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.16);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.28);
  svg { width: 26px; height: 26px; fill: #fff; }
}
.vl-logo--sm {
  width: 42px;
  height: 42px;
  background: linear-gradient(145deg, var(--vl-teal), var(--vl-teal-2));
  box-shadow: 0 6px 16px rgba(14, 124, 134, 0.3);
}
.vl-wordmark {
  display: flex;
  flex-direction: column;
  line-height: 1.15;
  font-size: 26px;
  font-weight: 700;
  small {
    font-size: 12.5px;
    font-weight: 500;
    letter-spacing: 0.02em;
    color: rgba(255, 255, 255, 0.72);
  }
}
.vl-wordmark--dark { color: var(--vl-ink); small { color: var(--vl-ink-2); } }

.vl-features {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 16px;
  li {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 15.5px;
    font-weight: 500;
  }
}
.vl-features__tick {
  flex: none;
  display: inline-grid;
  place-items: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.16);
  svg { width: 15px; height: 15px; fill: none; stroke: #fff; stroke-width: 2.2; stroke-linecap: round; stroke-linejoin: round; }
}

/* ───────── form panel ───────── */
.vl-panel {
  position: relative;
  display: grid;
  place-items: center;
  padding: 72px 28px 40px;
}
.vl-lang {
  position: absolute;
  top: 22px;
  inset-inline-end: 24px;
  display: inline-flex;
  padding: 3px;
  border: 1px solid var(--vl-line);
  border-radius: 999px;
  background: #fff;
  button {
    border: 0;
    background: transparent;
    padding: 5px 14px;
    border-radius: 999px;
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    color: var(--vl-ink-2);
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    &.on { background: var(--vl-teal); color: #fff; }
    &:not(.on):hover { color: var(--vl-teal); }
  }
}
.vl-card {
  width: 100%;
  max-width: 420px;
  padding: 38px 36px 34px;
  border: 1px solid var(--vl-line);
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(20, 32, 43, 0.04), 0 18px 48px rgba(20, 32, 43, 0.08);
  h1 { margin: 0 0 6px; font-size: 26px; font-weight: 700; letter-spacing: -0.01em; }
}
.vl-card__logo { display: none; align-items: center; gap: 12px; margin-bottom: 22px; }
.vl-sub { margin: 0 0 24px; color: var(--vl-ink-2); font-size: 14.5px; line-height: 1.7; }

.vl-error {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 18px;
  padding: 11px 14px;
  border-radius: 12px;
  background: #fdecee;
  color: #a1283a;
  font-size: 14px;
  font-weight: 500;
  svg { flex: none; width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
}

.vl-field {
  display: block;
  margin-bottom: 18px;
  > span { display: block; margin-bottom: 7px; font-size: 13.5px; font-weight: 600; color: var(--vl-ink); }
  input {
    width: 100%;
    height: 48px;
    padding: 0 14px;
    border: 1.5px solid var(--vl-line);
    border-radius: 12px;
    background: #fbfcfd;
    font: inherit;
    font-size: 15px;
    color: var(--vl-ink);
    text-align: start;
    transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
    &::placeholder { color: #a3aebb; }
    &:hover { border-color: #c5d0dc; }
    &:focus { outline: 0; background: #fff; border-color: var(--vl-teal); box-shadow: 0 0 0 4px rgba(14, 124, 134, 0.14); }
    &:disabled { opacity: 0.65; }
  }
}
.vl-field__pw { position: relative; input { padding-right: 46px; } }
.vl-field__eye {
  position: absolute;
  inset-block: 0;
  right: 4px;
  width: 42px;
  border: 0;
  background: transparent;
  display: grid;
  place-items: center;
  cursor: pointer;
  color: #7d8aa0;
  &:hover { color: var(--vl-teal); }
  svg { width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; }
}

.vl-submit {
  width: 100%;
  height: 50px;
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 0;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--vl-teal), var(--vl-teal-2));
  color: #fff;
  font: inherit;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 8px 20px rgba(14, 124, 134, 0.28);
  transition: transform 0.12s, box-shadow 0.15s, opacity 0.15s;
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 12px 26px rgba(14, 124, 134, 0.34); }
  &:active:not(:disabled) { transform: translateY(0); }
  &:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }
}
.vl-spin {
  width: 18px;
  height: 18px;
  border: 2.5px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: vl-rot 0.7s linear infinite;
}
@keyframes vl-rot { to { transform: rotate(360deg); } }

/* ───────── responsive ───────── */
@media (max-width: 900px) {
  .vl-page { grid-template-columns: 1fr; }
  .vl-brand { display: none; }
  .vl-card__logo { display: flex; }
  .vl-panel { padding-top: 76px; }
}
@media (max-width: 480px) {
  .vl-card { padding: 28px 22px 26px; border-radius: 16px; }
}
</style>
